import { gql } from '@apollo/client';
import * as Apollo from '@apollo/client';

/* ============================================================================
   Fragments — réutilisables dans les queries, mutations, et `cache.writeFragment`
   pour rester DRY. Chaque fragment liste l'ensemble des champs qu'on
   sélectionne pour un type donné. Ajouter un champ ici → propagé partout.
   ============================================================================ */

export const ConstraintScenarioFields = gql`
  fragment ConstraintScenarioFields on IndicatorScenarioSimulation {
    id
    scenarioId
    trend
    weight
    enabled
  }
`;

export const StakeScenarioFields = gql`
  fragment StakeScenarioFields on StakeScenarioSimulation {
    id
    scenarioId
    trend
    weight
    enabled
  }
`;

export const RiskScenarioFields = gql`
  fragment RiskScenarioFields on RiskScenarioSimulation {
    id
    scenarioId
    impact
    likelihood
    enabled
  }
`;

export const StakeholderScenarioFields = gql`
  fragment StakeholderScenarioFields on StakeholderScenarioSimulation {
    id
    scenarioId
    trend
    impact
    x
    y
    enabled
  }
`;

export const FishboneBranchScenarioFields = gql`
  fragment FishboneBranchScenarioFields on FishboneBranchScenarioSimulation {
    id
    scenarioId
    weight
    enabled
  }
`;

export const FishboneItemScenarioFields = gql`
  fragment FishboneItemScenarioFields on FishboneItemScenarioSimulation {
    id
    scenarioId
    trend
    completion
    weight
    enabled
  }
`;

export const IndicatorFields = gql`
  fragment IndicatorFields on Indicator {
    id
    projectId
    name
    description
    reference
    trend
    weight
    enabled
    indicatorCategoryId
    frequency
    createdAt
    evaluatedAt
    taggings {
      id
      objectId
      tagId
      type
      createdAt
    }
    fishboneLinks {
      id
      fishboneNodeId
      nodeType
      indicatorId
      strength
      createdAt
    }
    scenarios {
      ...ConstraintScenarioFields
    }
  }
  ${ConstraintScenarioFields}
`;

export const StakeFields = gql`
  fragment StakeFields on Stake {
    id
    projectId
    name
    description
    reference
    trend
    weight
    enabled
    createdAt
    evaluatedAt
    taggings {
      id
      objectId
      tagId
      type
      createdAt
    }
    fishboneLinks {
      id
      fishboneNodeId
      nodeType
      stakeId
      strength
      createdAt
    }
    impactedIndicators {
      id
      stakeId
      indicatorId
      strength
      createdAt
    }
    scenarios {
      ...StakeScenarioFields
    }
  }
  ${StakeScenarioFields}
`;

export const RiskFields = gql`
  fragment RiskFields on Risk {
    id
    projectId
    name
    description
    reference
    impact
    likelihood
    isOpportunity
    riskCategoryId
    enabled
    createdAt
    evaluatedAt
    impactedStakes {
      id
      stakeId
      riskId
      strength
      createdAt
    }
    impactedIndicators {
      id
      riskId
      indicatorId
      strength
      createdAt
    }
    openingLinks {
      id
      riskId
      openingId
      strength
      createdAt
    }
    fishboneLinks {
      id
      fishboneNodeId
      nodeType
      riskId
      strength
      createdAt
    }
    scenarios {
      ...RiskScenarioFields
    }
    taggings {
      id
      objectId
      tagId
      type
      createdAt
    }
  }
  ${RiskScenarioFields}
`;

export const StakeholderFields = gql`
  fragment StakeholderFields on Stakeholder {
    id
    name
    description
    icon
    impact
    trend
    x
    y
    enabled
    createdAt
    impactedIndicators {
      id
      stakeholderId
      indicatorId
      strength
    }
    impactedStakes {
      id
      stakeholderId
      stakeId
      strength
    }
    impactedRisks {
      id
      stakeholderId
      riskId
      strength
    }
    links {
      id
      originId
      targetId
      strength
    }
    fishboneLinks {
      id
      fishboneNodeId
      nodeType
      stakeholderId
      strength
      createdAt
    }
    scenarios {
      ...StakeholderScenarioFields
    }
    taggings {
      id
      objectId
      tagId
      type
    }
  }
  ${StakeholderScenarioFields}
`;

export const FishboneItemFields = gql`
  fragment FishboneItemFields on FishboneItem {
    id
    fishboneBranchId
    name
    description
    reference
    responsible
    trend
    weight
    position
    completion
    startOn
    endOn
    evaluatedAt
    enabled
    createdAt
    updatedAt
    icon
    taggings {
      id
      objectId
      tagId
      type
      createdAt
    }
    riskLinks {
      id
      fishboneNodeId
      nodeType
      riskId
      strength
      createdAt
    }
    stakeLinks {
      id
      fishboneNodeId
      nodeType
      stakeId
      strength
      createdAt
    }
    indicatorLinks {
      id
      fishboneNodeId
      nodeType
      indicatorId
      strength
      createdAt
    }
    stakeholderLinks {
      id
      fishboneNodeId
      nodeType
      stakeholderId
      strength
      createdAt
    }
    scenarios {
      ...FishboneItemScenarioFields
    }
  }
  ${FishboneItemScenarioFields}
`;

export const FishboneBranchFields = gql`
  fragment FishboneBranchFields on FishboneBranch {
    id
    projectId
    name
    description
    icon
    reference
    responsible
    weight
    position
    startOn
    endOn
    evaluatedAt
    enabled
    createdAt
    updatedAt
    taggings {
      id
      objectId
      tagId
      type
      createdAt
    }
    riskLinks {
      id
      fishboneNodeId
      nodeType
      riskId
      strength
      createdAt
    }
    stakeLinks {
      id
      fishboneNodeId
      nodeType
      stakeId
      strength
      createdAt
    }
    indicatorLinks {
      id
      fishboneNodeId
      nodeType
      indicatorId
      strength
      createdAt
    }
    stakeholderLinks {
      id
      fishboneNodeId
      nodeType
      stakeholderId
      strength
      createdAt
    }
    items {
      ...FishboneItemFields
    }
    scenarios {
      ...FishboneBranchScenarioFields
    }
  }
  ${FishboneItemFields}
  ${FishboneBranchScenarioFields}
`;

export const ScenarioFields = gql`
  fragment ScenarioFields on Scenario {
    id
    name
    description
    impacts
    enabled
    createdAt
  }
`;

export const RiskCategoryFields = gql`
  fragment RiskCategoryFields on RiskCategory {
    id
    name
    description
    weight
    createdAt
  }
`;

export const IndicatorCategoryFields = gql`
  fragment IndicatorCategoryFields on IndicatorCategory {
    id
    name
    description
    weight
    createdAt
  }
`;

export const TagFields = gql`
  fragment TagFields on Tag {
    id
    name
    description
    createdAt
  }
`;

/* ============================================================================
   Queries
   ============================================================================ */

export const listProjects = gql`
  query ListProjects {
    listProjects {
      items {
        id
        reference
        name
        description
        createdAt
        type
        readerCount
        myRole
      }
    }
  }
`;

// Projets partagés AVEC le caller (rôle reader/editor). Même forme que
// listProjects pour un rendu homogène des cards du portfolio.
export const listSharedProjects = gql`
  query ListSharedProjects {
    listSharedProjects {
      id
      project {
        id
        reference
        name
        description
        createdAt
        type
        readerCount
        myRole
      }
    }
  }
`;

export function useListSharedProjectsQuery() {
  return Apollo.useQuery(listSharedProjects);
}

// Partage : membres d'un projet (owner only côté resolver), + mutations.
export const projectMembers = gql`
  query ProjectMembers($id: ID!) {
    getProject(id: $id) {
      id
      members {
        id
        invitedEmail
        role
        status
        createdAt
      }
    }
  }
`;

export const addProjectMember = gql`
  mutation AddProjectMember($projectId: String!, $email: String!, $role: String, $locale: String) {
    addProjectMember(projectId: $projectId, email: $email, role: $role, locale: $locale) {
      id
      projectId
      invitedEmail
      role
      status
      createdAt
    }
  }
`;

export const removeProjectMember = gql`
  mutation RemoveProjectMember($id: ID!) {
    removeProjectMember(id: $id)
  }
`;

export const getProject = gql`
  query GetProject(
    $id: ID!
    $includeFishbone: Boolean = true
    $includeIndicators: Boolean = true
    $includeRisks: Boolean = true
    $includeStakeholders: Boolean = true
  ) {
    getProject(id: $id) {
      id
      name
      reference
      description
      descriptionLong
      vision
      type
      responsible
      createdAt
      myRole
      readerCount
      baselineScenarioDescription
      baselineScenarioImpacts
      analysisSummary
      fishboneDescription
      fishboneStartOn
      fishboneEndOn
      impacts {
        id
        name
      }
      likelihoods {
        id
        name
      }
      indicators @include(if: $includeIndicators) {
        ...IndicatorFields
      }
      fishboneBranchs @include(if: $includeFishbone) {
        ...FishboneBranchFields
      }
      stakes {
        ...StakeFields
      }
      riskCategories {
        ...RiskCategoryFields
      }
      indicatorCategories {
        ...IndicatorCategoryFields
      }
      risks @include(if: $includeRisks) {
        ...RiskFields
      }
      scenarios {
        ...ScenarioFields
      }
      stakeholders @include(if: $includeStakeholders) {
        ...StakeholderFields
      }
      tags {
        ...TagFields
      }
    }
  }
  ${IndicatorFields}
  ${FishboneBranchFields}
  ${StakeFields}
  ${RiskCategoryFields}
  ${IndicatorCategoryFields}
  ${RiskFields}
  ${ScenarioFields}
  ${StakeholderFields}
  ${TagFields}
`;

export const getCommentsQuery = gql`
  query GetComments($projectId: String!, $objectType: String!, $objectId: String!) {
    getComments(projectId: $projectId, objectType: $objectType, objectId: $objectId) {
      id
      description
      createdAt
      objectId
      objectType
      commentedAt
    }
  }
`;

export function useListProjectsQuery() {
  return Apollo.useQuery(listProjects);
}

const ProjectTypeHint = gql`
  fragment ProjectTypeHint on Project {
    id
    type
  }
`;

// Optimisation du fetch : selon le type de projet, on n'a pas besoin de toutes
// les sections (risks -> pas de fishbone/indicateurs ; stakeholders -> pas de
// fishbone/indicateurs/risques). Le type est lu dans le cache Apollo (deja
// charge par la liste portfolio listProjects). Type inconnu (deep-link, cache
// froid) -> on recupere tout (fallback sur les valeurs par defaut = true).
export function useGetProjectQuery(id: string) {
  const client = Apollo.useApolloClient();
  let type: string | undefined;
  try {
    const cached: any = client.readFragment({
      id: client.cache.identify({ __typename: 'Project', id }),
      fragment: ProjectTypeHint
    });
    type = cached?.type;
  } catch {
    // pas de hint en cache -> on garde le fallback (tout recuperer)
  }
  const isPro = type === 'pro';
  const unknown = type === undefined || type === null;
  return Apollo.useQuery(getProject, {
    variables: {
      id,
      includeFishbone: unknown || isPro,
      includeIndicators: unknown || isPro,
      includeRisks: unknown || isPro || type === 'risks',
      includeStakeholders: unknown || isPro || type === 'risks' || type === 'stakeholders'
    }
  });
}

export function useGetCommentsQuery(projectId: string, objectType: string, objectId: string) {
  return Apollo.useQuery(getCommentsQuery, {
    variables: {
      projectId,
      objectType,
      objectId
    }
  });
}

export const askBot = gql`
  query askBot($projectId: String, $question: String!, $threadId: String, $scenarioId: String, $screen: String, $language: String, $history: AWSJSON) {
    askBot(projectId: $projectId, question: $question, threadId: $threadId, scenarioId: $scenarioId, screen: $screen, language: $language, history: $history)
  }
`;

export const analyzeSituation = gql`
  query analyzeSituation(
    $projectId: String!
    $action: String
    $query: String
    $hypothesis: String
    $scenarioId: String
    $language: String
    $scenarios: AWSJSON
    $params: AWSJSON
  ) {
    analyzeSituation(
      projectId: $projectId
      action: $action
      query: $query
      hypothesis: $hypothesis
      scenarioId: $scenarioId
      language: $language
      scenarios: $scenarios
      params: $params
    )
  }
`;

export const suggestScenarioHypothesesQuery = gql`
  query suggestScenarioHypotheses($projectId: String!, $language: String) {
    suggestScenarioHypotheses(projectId: $projectId, language: $language)
  }
`;

export const subscribeToProjectChanges = gql`
  subscription onProjectChange($projectId: String!) {
    onProjectChange(projectId: $projectId) {
      projectId
      objectId
      type
      operation
      changes
      author
    }
  }
`;

export const subscribeToMessages = gql`
  subscription subscribeToMessages($projectId: String!) {
    onProjectMessage(projectId: $projectId) {
      projectId
      message
    }
  }
`;

export const getScenarioGenerationState = gql`
  query getScenarioGenerationState($projectId: String!, $scenarioId: String!) {
    getScenarioGenerationState(projectId: $projectId, scenarioId: $scenarioId) {
      scenarioId
      status
      step
      startedAt
      updatedAt
      info
      results {
        objectType
        modifications
        newElements
      }
      error
    }
  }
`;

export function useChangeSubscription(projectId: string) {
  return Apollo.useSubscription(subscribeToProjectChanges, {
    variables: {
      projectId
    }
  });
}

